import * as React from 'react';
import { IAButton } from '../button/Button';
import { IASearch } from '../search/Search';
import ImageService from '../../services/ImageService';
import { Environment } from '../../services/Environment';
import * as styles from './StockPhotos.css';
import { LocalizationService } from '../../services/LocalizationService';
import { IASpinner } from '../spinner/Spinner';
import { IASkeleton } from '../skeleton/Skeleton';

export interface IProps {
  open: boolean;
  close: () => void;
  onSelect: (image: string, lastModified?: string) => void;
  highlightColor: string;
  environment: "Development" | "Test" | "Production";
  tenant: string;
  token: string;
  isMobile: boolean;
  style?: React.CSSProperties;
  mobileImagesPerRow?: number;
}

export interface IState {
  searchQuery: string;
  images: any[];
  totalCount: number;
  loading: boolean;
}

export class StockPhotos extends React.Component<IProps, IState> {

  private readonly localizationService: LocalizationService = new LocalizationService();

  constructor(props: IProps) {
    super(props);
    this.state = {
      searchQuery: "",
      images: [],
      totalCount: undefined,
      loading: false
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  componentDidMount(): void {
    if (this.props.open) {
      this.search("", 0);
    }
    window.addEventListener('resize', () => this.forceUpdate());
    setTimeout(() => {
      this.forceUpdate();
    }, 0);
  }

  public componentWillReceiveProps(props: IProps): void {
    if (props.open && props.open !== this.props.open) {
      this.search("", 0);
    }
  }

  private search(searchQuery: string, skip: number): void {
    this.setState({ loading: true });
    setTimeout(() => {
      if (searchQuery === this.state.searchQuery) {
        ImageService.getStockImages(Environment.getEnvironmentForImageAPI(this.props.environment), this.props.tenant, this.props.token, searchQuery, 50, skip, this.localizationService.getLanguageCode()).then((result: any) => {
          if (result && result.images && this.state.searchQuery === result.searchQuery) {
            if (skip === 0) {
              this.setState({ images: result.images, totalCount: result.count }, () => this.forceUpdate());
            } else {
              this.setState({ images: this.state.images.concat(result.images), totalCount: result.count }, () => this.forceUpdate());
            }
          } else {
            this.setState({ images: [] });
          }
        }).finally(() => this.setState({loading: false}));
      }
    }, 500);
  }

  public render(): JSX.Element {
    const thumbnails = [];
    const stockPhotosContainer = document.getElementById("IA_StockPhotos");
    let stockPhotosContainerWidth;
    if (stockPhotosContainer) {
      stockPhotosContainerWidth = stockPhotosContainer.clientWidth - 82;
    } else {
      setTimeout(() => {
        this.forceUpdate();
      }, 500);
    }
    let row = [];
    let imagesDisplayed = 0;
    let imagesInRow = [];
    let imagesCombinedWidthInRow = 0;
    this.state.images.forEach((image: any) => {
      imagesDisplayed++;
      if (imagesInRow.length === (this.props.isMobile ? (this.props.mobileImagesPerRow ?? 2) : 4)) {
        const factor = (this.props.isMobile ? window.innerWidth - 65 : stockPhotosContainerWidth) / imagesCombinedWidthInRow;
        const height = 130 * factor;
        imagesInRow.forEach((imageInRow: any) => {
          row.push(
            <img
              key={imageInRow.Id}
              className={styles.IA_thumbnail}
              alt={imageInRow.Description || ""}
              width={imageInRow.ThumbnailInfo.Medium.Width * factor}
              height={height}
              style={{
                width: imageInRow.ThumbnailInfo.Medium.Width * factor
              }}
              src={`https://cdn.hubblecontent.osi.office.net/m365content/publish/${imageInRow.Id}/thumbnails/large.jpg`}
              onClick={() => this.props.onSelect(`https://cdn.hubblecontent.osi.office.net/${imageInRow.Path}`)}
            />
          );
        });
        thumbnails.push(
          <div
            key={`IA_row_${image.Id}`}
            className={styles.IA_imageRow}
            style={{
              height: `${height}px`
            }}>
            {row}
          </div>
        );
        row = [];
        imagesInRow = [];
        imagesInRow.push(image);
        imagesCombinedWidthInRow = image.ThumbnailInfo.Medium.Width;
      } else {
        imagesInRow.push(image);
        imagesCombinedWidthInRow = imagesCombinedWidthInRow + image.ThumbnailInfo.Medium.Width;
        if (imagesDisplayed === this.state.totalCount) {
          imagesInRow.forEach((imageInRow: any) => {
            row.push(
              <img
                key={imageInRow.Id}
                className={styles.IA_thumbnail}
                alt={imageInRow.Description || ""}
                height={130}
                src={`https://cdn.hubblecontent.osi.office.net/m365content/publish/${imageInRow.Id}/thumbnails/large.jpg`}
                onClick={() => this.props.onSelect(`https://cdn.hubblecontent.osi.office.net/${imageInRow.Path}`)}
              />
            );
          });
          thumbnails.push(
            <div
              key={`IA_row_${image.Id}`}
              className={styles.IA_imageRow}
              style={{
                height: `${130}px`
              }}>
              {row}
            </div>
          );
        }
      }
    });
    return (
      <div
        id="IA_StockPhotos"
        style={{
          padding: 20,
          width: "100%",
          boxSizing: "border-box",
          float: "left",
          position: "relative",
          ...this.props.style
        }}>
        <IASearch
          searchQuery={this.state.searchQuery}
          placeholder={this.localizationService.strings.StockPhotos_SearchPlaceholder}
          arialable="Search"
          // dataAutomationId=""
          borderRadius={5}
          onChange={(searchQuery: string) => {
            this.search(searchQuery, 0);
            this.setState({ searchQuery });
          }}
        />
        <div className={styles.IA_images}>
          {thumbnails}
        </div>
        {this.state.images.length === 0 && this.state.searchQuery !== "" && !this.state.loading &&
          <div className={styles.IA_imageResultInfo}>{`${this.localizationService.strings.StockPhotos_NoImagesForTheResult} "${this.state.searchQuery}"`}</div>
        }
        <div
          style={{
            clear: "both",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingBottom: 20
          }}
        >
          {this.state.loading &&
            <>
              {this.state.images?.length > 0
              ?
                <IASpinner
                  color={this.props.highlightColor}
                  style={{
                    marginTop: 10,
                    marginBottom: 10
                  }}
                />
              :
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    width: "100%"
                  }}
                >
                  {(Array.from(Array(7).keys())).map(i => {
                    const width = "calc(25% - 10px)";
                    const height = 175;
                    return <IASkeleton
                      key={i}
                      skeleton={{
                        height,
                        width,
                        marginBottom: 10,
                        marginLeft: 5,
                        marginRight: 5,
                        borderRadius: 5
                      }}
                    />
                  })}
                </div>
              }
            </>
          }
          {this.state.images.length > 0 && !this.state.loading &&
            <div className={styles.IA_imageResultInfo}>{this.state.searchQuery === "" ? `${this.localizationService.strings.StockPhotos_Showing} ${this.state.images.length} ${this.localizationService.strings.StockPhotos_OutOf} ${this.state.totalCount} ${this.localizationService.strings.StockPhotos_Photos}` : `${this.localizationService.strings.StockPhotos_Showing} ${this.state.images.length} ${this.localizationService.strings.StockPhotos_OutOf} ${this.state.totalCount} ${this.localizationService.strings.StockPhotos_ResultsForTheSearch} "${this.state.searchQuery}"`}</div>
          }
          {this.state.images.length < this.state.totalCount &&
            <IAButton
              label={this.localizationService.strings.StockPhotos_LoadMore}
              buttonColor={this.props.highlightColor}
              showSpinner={false}
              onClick={() => {
                this.search(this.state.searchQuery, this.state.images.length);
              }}
              borderRadius={5}
              style={{
                marginTop: 15,
                width: 100,
              }}
            />
          }
        </div>
      </div>
    )
  }
} 